// src/components/ProcurationModal.js
import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  Pressable,
  Image,
  Platform,
} from 'react-native';
import { useTranslation } from '../i18n';

const COLORS = {
  primary: "#14B8A6",
  secondary: "#7C3AED",
  background: "#F7F9FA",
  card: "#FFFFFF",
  textDark: "#111827",
  textMuted: "#6B7280",
  border: "#E5E7EB",
  warning: "#F59E0B",
  warningLight: "#FEF3C7",
  danger: "#DC2626",
};

export default function ProcurationModal({
  visible,
  onDismiss,
  onContinueWithout,
  onUploadDocument,
}) {
  const { t } = useTranslation();

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onDismiss}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalCard}>
          {/* Warning Icon */}
          <View style={styles.iconContainer}>
            <View style={styles.warningCircle}>
              <Text style={styles.warningIcon}>⚠️</Text>
            </View>
          </View>

          {/* Title */}
          <Text style={styles.modalTitle}>
            {t("procurationModal.title", "Document de procuration requis")}
          </Text>

        

          {/* Important Note */}
          <View style={styles.noteBox}>
            <Text style={styles.noteTitle}>
              {t("procurationModal.noteTitle", "📌 Important")}
            </Text>
            <Text style={styles.noteText}>
              {t(
                "procurationModal.noteText",
                "Vous pouvez continuer sans procuration, mais vous ne pourrez pas obtenir le QR code du véhicule. Vous devrez fournir ce document ultérieurement pour finaliser la vérification du véhicule."
              )}
            </Text>
          </View>

          {/* Buttons */}
          <View style={styles.buttonContainer}>
            <Pressable
              style={({ pressed }) => [
                styles.uploadButton,
                pressed && styles.buttonPressed,
              ]}
              onPress={onUploadDocument}
            >
              <Text style={styles.uploadButtonText}>
                📄 {t("procurationModal.uploadNow", "Ajouter la procuration")}
              </Text>
              <Text style={styles.uploadButtonSubtext}>
                {t("procurationModal.uploadHint", "PDF, JPG ou PNG")}
              </Text>
            </Pressable>

            <Pressable
              style={({ pressed }) => [
                styles.continueButton,
                pressed && styles.buttonPressed,
              ]}
              onPress={onContinueWithout}
            >
              <Text style={styles.continueButtonText}>
                {t("procurationModal.continueWithout", "Continuer sans procuration")}
              </Text>
              <Text style={styles.continueButtonSubtext}>
                {t("procurationModal.continueHint", "QR code véhicule non généré")}
              </Text>
            </Pressable>

            <Pressable
              style={({ pressed }) => [
                styles.cancelButton,
                pressed && styles.buttonPressed,
              ]}
              onPress={onDismiss}
            >
              <Text style={styles.cancelButtonText}>
                {t("common.cancel", "Annuler")}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(17, 24, 39, 0.75)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  modalCard: {
    backgroundColor: COLORS.card,
    borderRadius: 24,
    padding: 24,
    width: "100%",
    maxWidth: 500,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  iconContainer: {
    alignItems: "center",
    marginBottom: 16,
  },
  warningCircle: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: COLORS.warningLight,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 2,
    borderColor: COLORS.warning,
  },
  warningIcon: {
    fontSize: 32,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: "800",
    color: COLORS.textDark,
    textAlign: "center",
    marginBottom: 12,
  },
  modalDescription: {
    fontSize: 15,
    color: COLORS.textMuted,
    textAlign: "center",
    lineHeight: 22,
    marginBottom: 20,
  },
  noteBox: {
    backgroundColor: COLORS.warningLight,
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: COLORS.warning,
  },
  noteTitle: {
    fontSize: 14,
    fontWeight: "700",
    color: COLORS.warning,
    marginBottom: 6,
  },
  noteText: {
    fontSize: 13,
    color: COLORS.textDark,
    lineHeight: 18,
  },
  buttonContainer: {
    gap: 12,
  },
  uploadButton: {
    backgroundColor: COLORS.primary,
    borderRadius: 12,
    padding: 16,
    alignItems: "center",
  },
  uploadButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "700",
    marginBottom: 4,
  },
  uploadButtonSubtext: {
    color: "rgba(255,255,255,0.9)",
    fontSize: 12,
  },
  continueButton: {
    backgroundColor: "#F3F4F6",
    borderRadius: 12,
    padding: 16,
    alignItems: "center",
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  continueButtonText: {
    color: COLORS.textDark,
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 4,
  },
  continueButtonSubtext: {
    color: COLORS.textMuted,
    fontSize: 12,
  },
  cancelButton: {
    padding: 12,
    alignItems: "center",
  },
  cancelButtonText: {
    color: COLORS.textMuted,
    fontSize: 14,
    fontWeight: "600",
  },
  buttonPressed: {
    opacity: 0.7,
    transform: [{ scale: 0.98 }],
  },
});